import axios from 'axios';
import { getBasePath, getLoginPath } from '../utils/pathUtils';

const SESSION_KEY_API_BASE_URL = 'ctms_api_base_url';
const SESSION_KEY_SS_WEB_CORE_TOKEN = 'ctms_ss_web_core_token';

// SS.WebCore required headers. CallType and sAuthenticationHeader are sent by the client.
// User-Agent cannot be set from browser JS (forbidden header); the API middleware injects "ms web api client protocol" when missing (same as Ramp).
const SS_WEB_CORE_CALL_TYPE = '-999';
const SS_WEB_CORE_USER_AGENT = 'ms web api client protocol';

// Get base URL from session (used after first load)
function getBaseUrlFromSession() {
  try {
    return sessionStorage.getItem(SESSION_KEY_API_BASE_URL) || '';
  } catch {
    return '';
  }
}

// SS.WebCore expects sAuthenticationHeader to be valid Base-64 only; stray characters cause it to throw and append error to response.
function sanitizeBase64Token(value) {
  if (value == null || typeof value !== 'string') return '';
  return value.trim().replace(/[^A-Za-z0-9+/=]/g, '');
}

function getSSWebCoreToken() {
  try {
    return sessionStorage.getItem(SESSION_KEY_SS_WEB_CORE_TOKEN) || '';
  } catch {
    return '';
  }
}

// Fetch SS.WebCore encrypted token from API (middleware injects it for this path so no token needed).
// Use plain fetch to avoid interceptor recursion. Call with CallType + User-Agent only.
let clientTokenFetchPromise = null;
async function fetchClientTokenAndStore(baseURL) {
  if (!baseURL || typeof baseURL !== 'string') return null;
  if (clientTokenFetchPromise) return clientTokenFetchPromise;
  clientTokenFetchPromise = (async () => {
    const url = `${baseURL.replace(/\/+$/, '')}/Auth/ClientToken`;
    const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      CallType: SS_WEB_CORE_CALL_TYPE,
      // User-Agent: cannot set in browser (forbidden header); API injects it when missing
    },
  });
  if (!res.ok) return null;
  const data = await res.json();
  const token = data?.token != null ? sanitizeBase64Token(String(data.token)) : null;
  if (token) {
    try {
      sessionStorage.setItem(SESSION_KEY_SS_WEB_CORE_TOKEN, token);
    } catch (e) {
      console.warn('Could not store SS WebCore token', e);
    }
    return token;
  }
  clientTokenFetchPromise = null;
  return null;
  })();
  try {
    return await clientTokenFetchPromise;
  } finally {
    if (!getSSWebCoreToken()) clientTokenFetchPromise = null;
  }
}

// First time: read config.json, get API_BASE_URL, store in sessionStorage.
let configLoaded = false;
let configObject = null;
const configPromise = (async () => {
  try {
    const basePath = typeof window !== 'undefined' ? (getBasePath() || '') : '';
    const configUrl = basePath ? `${basePath}/config.json` : 'config.json';
    const response = await fetch(configUrl);
    if (response.ok) {
      const config = await response.json();
      configObject = config;
      if (config.API_BASE_URL) {
        try {
          sessionStorage.setItem(SESSION_KEY_API_BASE_URL, config.API_BASE_URL);
        } catch (e) {
          console.warn('Could not store config in session', e);
        }
        configLoaded = true;
        return config.API_BASE_URL;
      }
    }
  } catch (error) {
    console.warn('Failed to load config.json', error);
  }
  configLoaded = true;
  return '';
})();

const api = axios.create({
  baseURL: '', // Set per-request from session or config
  timeout: 50000,
  withCredentials: true, // send session cookie for server-side session
  headers: {
    'Content-Type': 'application/json',
    CallType: SS_WEB_CORE_CALL_TYPE,
    // User-Agent: not set here (browser forbids it); API middleware injects "ms web api client protocol"
  },
});

// Every request: add SS.WebCore headers and use URL from session/config.
api.interceptors.request.use(
  async (requestConfig) => {
    let baseURL = getBaseUrlFromSession();
    if (!baseURL) {
      baseURL = await configPromise;
    }
    if (baseURL && requestConfig.url) {
      const base = baseURL.replace(/\/+$/, '');
      const path = requestConfig.url.replace(/^\/+/, '');
      requestConfig.url = path ? `${base}/${path}` : base;
      requestConfig.baseURL = '';
    }

    const isClientTokenRequest = requestConfig.url && requestConfig.url.includes('Auth/ClientToken');

    // Send CallType (and sAuthenticationHeader for non-ClientToken requests). User-Agent is set by API when missing.
    requestConfig.headers.CallType = SS_WEB_CORE_CALL_TYPE;

    // sAuthenticationHeader: for Auth/ClientToken we leave it out (middleware injects it). For all others, use stored token or fetch it first.
    if (!isClientTokenRequest) {
      let token = getSSWebCoreToken();
      if (!token) {
        token = await fetchClientTokenAndStore(baseURL || requestConfig.url?.replace(/\/Auth\/.*$/, '') || '');
      }
      if (token) {
        requestConfig.headers.sAuthenticationHeader = sanitizeBase64Token(token);
      }
    }

    return requestConfig;
  },
  (error) => Promise.reject(error)
);

export function clearClientStorage() {
  try {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('projectInfo');
    localStorage.removeItem('currentProject');
    localStorage.removeItem('auditValues');
    sessionStorage.removeItem(SESSION_KEY_SS_WEB_CORE_TOKEN);
  } catch (e) {
    console.warn('clearClientStorage:', e);
  }
}

/**
 * Single AJAX call for Gateway login with sHeader and all required data.
 * Ensures SS.WebCore token (sAuthenticationHeader) and CallType are sent; User-Agent is set by API.
 * POSTs to /API/Login/Login with form data; returns JSON when Accept: application/json.
 * @param {Object} credentials - { globaluserid, aplicationid, token?, roleid?, projectcode? }
 * @returns {Promise<{ data: { success, redirectUrl, user, project } }>}
 */
export async function gatewayLogin(credentials = {}) {
  let baseURL = getBaseUrlFromSession();
  if (!baseURL) baseURL = await configPromise;
  let token = getSSWebCoreToken();
  if (!token && baseURL) token = await fetchClientTokenAndStore(baseURL);
  if (token) {
    try {
      sessionStorage.setItem(SESSION_KEY_SS_WEB_CORE_TOKEN, sanitizeBase64Token(token));
    } catch (_) {}
  }

  const params = {};
  if (credentials.globaluserid != null) params.globaluserid = String(credentials.globaluserid).trim();
  if (credentials.aplicationid != null) params.aplicationid = String(credentials.aplicationid).trim();
  if (credentials.token != null) params.token = String(credentials.token).trim();
  if (credentials.roleid != null) params.roleid = String(credentials.roleid).trim();
  if (credentials.projectcode != null) params.projectcode = String(credentials.projectcode).trim();

  return api.post('API/Login/Login', new URLSearchParams(params), {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });
}

api.interceptors.response.use(
  (response) => {
    const data = response.data;
    if (data != null && typeof data === 'string' && data.includes('"success"') && (data.startsWith('The input is not a valid Base-64') || data.startsWith('The input data is not a complete block'))) {
      const jsonStart = data.indexOf('{');
      if (jsonStart >= 0) {
        try {
          response.data = JSON.parse(data.substring(jsonStart));
        } catch (_) { /* leave as-is */ }
      }
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const data = error.response?.data;
      if (data && (data.clearClientStorage === true || data.clearClientStorage === 'true')) {
        clearClientStorage();
      }
      const isCheckSession = error.config?.url?.includes('Auth/CheckSession');
      if (!isCheckSession) {
        const baseURL = error.config?.baseURL || getBaseUrlFromSession();
        if (baseURL) {
          const redirectUrl = baseURL.replace(/\/$/, '') + '/Auth/LogoutRedirectUrl';
          fetch(redirectUrl, { credentials: 'include' })
            .then((r) => r.ok ? r.json() : Promise.reject())
            .then((data) => {
              const target = data?.logoutRedirectUrl?.trim();
              window.location.href = target || getLoginPath();
            })
            .catch(() => {
              window.location.href = getLoginPath();
            });
        } else {
          window.location.href = getLoginPath();
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
